import type { ScanAgeBucket } from "#/types/report";
import DonutChart from "#/components/ui/DonutChart";
import { EmptyState } from "#/components/ui/EmptyState";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";

const BUCKET_COLORS = [
  "var(--mantine-color-blue-5)",
  "var(--mantine-color-green-5)",
  "var(--mantine-color-yellow-5)",
  "var(--mantine-color-red-5)",
  "var(--mantine-color-gray-5)",
];

interface ScanAgeDistributionProps {
  buckets: ScanAgeBucket[];
  tooltip?: ReactNode;
}

const ScanAgeDistribution = ({ buckets, tooltip }: ScanAgeDistributionProps) => {
  const { t } = useTranslation();

  const hasData = buckets.some((b) => b.count > 0);
  const chartData = buckets.map((bucket, index) => ({
    name: bucket.label,
    value: bucket.count,
    color: BUCKET_COLORS[index % BUCKET_COLORS.length],
  }));

  return (
    <ReportSection title={t("reports.inventoryScan.scanAgeDistribution.title")} tooltip={tooltip}>
      {!hasData ? (
        <div className="flex h-48 items-center justify-center">
          <EmptyState title={t("common.noData")} />
        </div>
      ) : (
        <div className="flex items-center gap-6">
          <div className="h-48 w-48 shrink-0">
            <DonutChart data={chartData} size={190} thickness={55} />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {chartData.map((item) => (
              <div
                key={item.name}
                className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-700">{item.name}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ReportSection>
  );
};

export default ScanAgeDistribution;
